import { Trans, t } from '@lingui/macro'
import { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { Paper, Typography, Box } from '@mui/material'
import { AIUsageStatistics } from '@/core/api/statistics'
import { ChartSkeleton } from './ChartSkeleton'
import { getDefaultLineChartOptions, formatChartDateLabels, createLineDataset } from '../lib/chartHelpers'
import { STATISTICS_CONSTANTS } from '../lib/constants'

type AIUsageByDayComponentProps = {
  data: AIUsageStatistics | undefined
  isLoading: boolean
}

export function AIUsageByDayComponent({ data, isLoading }: AIUsageByDayComponentProps) {
  const transcriptionsLabel = t`Transcriptions`
  const reportsLabel = t`Reports`
  const chartData = useMemo(() => {
    if (!data) return null

    const allDays = new Set([...data.transcriptions.byDay.map((d) => d.day), ...data.reports.byDay.map((d) => d.day)])
    const sortedDays = Array.from(allDays).sort()
  
    const transcriptionCounts = sortedDays.map((day) => {
      const item = data.transcriptions.byDay.find((d) => d.day === day)
      return item ? item.count : 0
    })
   
    const reportCounts = sortedDays.map((day) => {
      const item = data.reports.byDay.find((d) => d.day === day)
      return item ? item.count : 0
    })
    
    const formattedLabels = formatChartDateLabels(sortedDays)

    return {
      labels: formattedLabels,
      datasets: [
        createLineDataset(transcriptionsLabel, transcriptionCounts, 'primary', 'primaryBackground'),
        createLineDataset(reportsLabel, reportCounts, 'secondary', 'secondaryBackground'),
      ],
    }
  }, [data, reportsLabel, transcriptionsLabel])

  if (isLoading) {
    return <ChartSkeleton />
  }

  if (!chartData) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          <Trans>No data available</Trans>
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        <Trans>AI usage over time</Trans>
      </Typography>
      <Box sx={{ height: STATISTICS_CONSTANTS.DEFAULT_CHART_HEIGHT }}>
        <Line data={chartData} options={getDefaultLineChartOptions()} />
      </Box>
    </Paper>
  )
}
